/*
 * File: InputNumberFormat.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Renders an Material UI text field for handling numeric input
 */
import React from "react";
import NumberFormat from "react-number-format";
import PropTypes from "prop-types";
import TextField from "@material-ui/core/TextField";
import { observer } from "mobx-react";
import FilledInput from "@material-ui/core/FilledInput";

const NumberFormatCustom = ({
  allowDecimals,
  decimalScale,
  setErrorMessage,
  inputRef,
  onChange,
  max,
  ...other
}) => {
  /**
   * Attempts to validate the new value of the field.
   *
   * @param {string} val The string value of the text field
   * @returns {string | null} An error to display, or null if the input validated successfully
   */
  const validate = val => {
    if (!max) {
      return null;
    }

    let numberVal = Number(val);
    let numberMax = Number(max);
    if (numberVal > numberMax) {
      return `Cannot be greater than "${max}"`;
    } else {
      return null;
    }
  };

  let finalDecimalScale = undefined;
  if (decimalScale && allowDecimals === true) {
    finalDecimalScale = decimalScale;
  }

  return (
    <NumberFormat
      {...other}
      isNumericString
      getInputRef={inputRef}
      decimalScale={finalDecimalScale}
      onValueChange={values => {
        setErrorMessage(validate(values.value));
        onChange({
          target: {
            value: values.value,
            name: other.name
          }
        });
      }}
    />
  );
};

NumberFormatCustom.propTypes = {
  inputRef: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  allowDecimals: PropTypes.bool,
  decimalScale: PropTypes.number,
  setErrorMessage: PropTypes.func.isRequired,
  max: PropTypes.string
};

const InputNumberFormat = ({
  field,
  value,
  error,
  parentOnChange,
  parentOnBlur,
  allowDecimals,
  setErrorMessage
}) => {
  if (Number.isInteger(value)) {
    value = value.toString();
  }

  return (
    <TextField
      error={error}
      onChange={parentOnChange}
      onBlur={parentOnBlur}
      id={field.name}
      name={field.name}
      value={value}
      disabled={field.disabled}
      autoComplete="off"
      // eslint-disable-next-line
      InputProps={{
        inputComponent: NumberFormatCustom,
        ...field.materialInputProps
      }}
      // eslint-disable-next-line
      inputProps={{ ...field.htmlInputProps, allowDecimals, setErrorMessage }}
      label={field.label}
      input={<FilledInput name={field.label} id={field.label} />}
      variant="filled"
    />
  );
};

InputNumberFormat.propTypes = {
  field: PropTypes.object.isRequired,
  error: PropTypes.bool,
  parentOnChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentOnBlur: PropTypes.func,
  allowDecimals: PropTypes.bool,
  setErrorMessage: PropTypes.func.isRequired
};

export default observer(InputNumberFormat);
